import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Stack,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Box,
  Icon,
  TableContainer,
  Divider,
  useMediaQuery
} from 'investira.react.components';
import { validators } from 'investira.sdk';
import { MelhorValorTableCell, PessoaImage, PrecisionNumberDisplay } from '../../molecules';
import services from '../../../services';
import utils from '../../../utils';

export const ATIVO_METRICA_RISCO = {
  volatilidade: 'volatilidade',
  trackingError: 'tracking_error'
};

const wMetricas = {
  [ATIVO_METRICA_RISCO.volatilidade]: {
    campo: ATIVO_METRICA_RISCO.volatilidade,
    criterion: 'lower',
    descricao: 'Volatilidade',
    tooltip: (
      <React.Fragment>
        <Typography color="inherit" variant="caption" component="p" gutterBottom>
          <b>Volatilidade</b>
        </Typography>
        <Typography color="inherit" variant="caption" component="p" gutterBottom>
          Mede a magnitude das oscilações de preço (desvio padrão dos retornos).
        </Typography>
        <Typography color="inherit" variant="caption" component="p" sx={{ fontWeight: 500 }}>
          Valores de Referência:
        </Typography>
        <Typography color="inherit" variant="caption" component="p" gutterBottom>
          Mínimo: Volatilidade próxima de 0 é típica de ativos estáveis (ex.: títulos públicos).
        </Typography>
        <Typography color="inherit" variant="caption" component="p" sx={{ fontWeight: 500 }}>
          Ideal:
        </Typography>
        <Typography color="inherit" variant="caption" component="p">
          {`Baixa: <10% anual (ex.: renda fixa ou ações defensivas).`}
        </Typography>
        <Typography color="inherit" variant="caption" component="p">
          {`Moderada: 10-20% anual (ex.: índices de mercado como Ibovespa ou S&P 500).`}
        </Typography>
        <Typography color="inherit" variant="caption" component="p">
          {`Alta: >20% anual (ex.: ações de tecnologia ou mercados emergentes).`}
        </Typography>
        <Typography color="inherit" variant="caption" component="p" gutterBottom>
          {`Máximo: Sem limite, mas volatilidades acima de 50% são extremas (ex.: criptomoedas).`}
        </Typography>
        <Typography color="inherit" variant="caption" component="p">
          Maior volatilidade implica maior risco. Comparar com a volatilidade do mercado ou setor
          para contextualizar.
        </Typography>
      </React.Fragment>
    )
  },
  [ATIVO_METRICA_RISCO.trackingError]: {
    campo: ATIVO_METRICA_RISCO.trackingError,
    criterion: 'higher',
    descricao: 'Tracking Error',
    tooltip: (
      <React.Fragment>
        <Typography color="inherit" variant="caption" component="p" gutterBottom>
          <b>Tracking Error</b>
        </Typography>
        <Typography color="inherit" variant="caption" component="p" gutterBottom>
          Mede a volatilidade da diferença de retornos entre uma carteira e seu benchmark,
          refletindo o grau de gestão ativa e o risco de desvio do índice de referência.
        </Typography>
      </React.Fragment>
    )
  }
};

const wPeriodos = [
  { campo: 'anual', descricao: 'Anual' },
  { campo: 'mensal', descricao: 'Mensal' },
  { campo: 'diario', descricao: 'Diário' }
];

/** Carrega e apresenta métricas periódicas de risco para uma lista ordenada de ativos. */
const AtivoMetricasRisco = memo(
  ({
    ativos = [],
    metricas = [ATIVO_METRICA_RISCO.volatilidade, ATIVO_METRICA_RISCO.trackingError],
    highlightEnabled = false,
    subheaderEnabled = true
  }) => {
    const requestCycleRef = useRef(0);
    const [dadosPorId, setDadosPorId] = useState({});
    const isUpSm = useMediaQuery(pTheme => pTheme.breakpoints.up('sm'));
    const xAtivosRecebidos = useMemo(
      () => ativos.filter(pAtivo => pAtivo?.ifeed_ativo_id),
      [ativos]
    );
    const xAtivoIds = useMemo(
      () => xAtivosRecebidos.map(pAtivo => pAtivo.ifeed_ativo_id),
      [xAtivosRecebidos]
    );
    const xAtivoIdsKey = xAtivoIds.join(',');
    const xMetricas = metricas.map(pMetrica => wMetricas[pMetrica]).filter(Boolean);
    const xAtivos = xAtivosRecebidos
      .map(xAtivo => {
        const xAtivoId = xAtivo.ifeed_ativo_id;
        const xDado = dadosPorId[xAtivoId];

        if (!xDado || xDado.loading) {
          return null;
        }

        return {
          ...xDado,
          id: xAtivoId,
          nome: xAtivo.ticker || xAtivo.ativo_seuid || xAtivo.ativo || String(xAtivoId),
          raw: xAtivo
        };
      })
      .filter(Boolean);

    /** Atualiza somente o ativo indicado, preservando as demais avaliações. */
    const pvUpdateAtivo = useCallback((pId, pPayload) => {
      setDadosPorId(pAnterior => ({
        ...pAnterior,
        [pId]: { ...pAnterior[pId], ...pPayload }
      }));
    }, []);

    useEffect(() => {
      const xCycle = requestCycleRef.current + 1;
      requestCycleRef.current = xCycle;
      setDadosPorId(
        xAtivoIds.reduce(
          (pResultado, pId) => ({
            ...pResultado,
            [pId]: { avaliacao: {}, loading: true }
          }),
          {}
        )
      );

      xAtivoIds.forEach(xId => {
        services.ativosbase.avaliacao(
          { ifeed_ativo_id: xId },
          rRes => {
            if (requestCycleRef.current !== xCycle) return;
            pvUpdateAtivo(xId, {
              avaliacao: rRes.data?.[0]?.avaliacao || {},
              loading: false
            });
          },
          () => {
            if (requestCycleRef.current !== xCycle) return;
            pvUpdateAtivo(xId, { avaliacao: {}, error: true, loading: false });
          }
        );
      });

      return () => {
        requestCycleRef.current += 1;
      };
    }, [pvUpdateAtivo, xAtivoIdsKey]);

    if (validators.isEmpty(xAtivos)) {
      return null;
    }

    return (
      <Stack flexGrow={1}>
        <Card component="section">
          <CardHeader
            title={
              <Stack spacing={2}>
                <Stack direction="row" gap={1} alignItems="center">
                  <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 600 }}>
                    Indicadores de Risco
                  </Typography>

                  <Tooltip
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 'none'
                        }
                      }
                    }}
                    title={
                      <Stack direction="row" spacing={4} p={1}>
                        {Object.values(wMetricas).map(pMetrica => (
                          <Stack key={pMetrica.campo} sx={{ width: 360 }}>
                            {pMetrica.tooltip}
                          </Stack>
                        ))}
                      </Stack>
                    }
                    arrow>
                    <Box component="span">
                      <Icon iconName="information" size={16} color="primary" />
                    </Box>
                  </Tooltip>
                </Stack>
              </Stack>
            }
            subheader={
              subheaderEnabled && (
                <Stack direction="row" gap={1} sx={{ py: 1 }}>
                  {xAtivos.map(pAtivo => (
                    <Chip
                      key={pAtivo.id}
                      label={utils.helpers.getAtivoDescricao(pAtivo.raw)}
                      variant="outlined"
                      color="primary"
                      size="small"
                    />
                  ))}
                </Stack>
              )
            }
          />
          <CardContent>
            <Stack sx={{ pt: 0 }}>
              <TableContainer id="table-container" sx={{ overflow: 'auto' }}>
                <Table sx={{ minWidth: 650 }} size="small" tableLayout="fixed">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          backgroundColor: pTheme => pTheme.palette.secondary.dark
                        }}
                      />
                      {wPeriodos.map(pPeriodo => (
                        <TableCell align="right" key={pPeriodo.campo}>
                          {pPeriodo.descricao}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {xMetricas.map(pMetrica => {
                      return (
                        <React.Fragment key={pMetrica.campo}>
                          <TableRow>
                            <TableCell
                              component="th"
                              scope="row"
                              colSpan={isUpSm ? 4 : 1}
                              sx={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 1,
                                backgroundColor: pTheme => pTheme.palette.secondary.dark
                              }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {pMetrica.descricao}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {xAtivos.map(pAtivo => {
                            const xValores = pAtivo.avaliacao?.[pMetrica.campo];

                            const pvDescricao = () => {
                              if (xAtivos.length < 1) {
                                return null;
                              }

                              const xAtivoDescricao = utils.helpers.getAtivoDescricao(pAtivo.raw);

                              const xAtivoLabel = isUpSm
                                ? xAtivoDescricao
                                : utils.typo.shortname(xAtivoDescricao, 25);

                              return (
                                <Stack direction="row" gap={1}>
                                  <Stack direction="row" gap={1}>
                                    {pAtivo?.raw?.logo_pessoa_id && (
                                      <PessoaImage
                                        logoPessoaId={pAtivo.raw.logo_pessoa_id}
                                        alt={pAtivo.raw.pessoa}
                                        hasLogo={pAtivo.raw.logo_ok}
                                        size={18}
                                      />
                                    )}
                                    <Stack>{xAtivoLabel}</Stack>
                                  </Stack>
                                </Stack>
                              );
                            };

                            return (
                              <TableRow key={`${pAtivo.id}-${pMetrica.campo}`}>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1,
                                    backgroundColor: pTheme => pTheme.palette.secondary.dark,
                                    maxWidth: !isUpSm ? '200px' : 'none'
                                  }}>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography variant="body2" noWrap>
                                      {pvDescricao()}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                {wPeriodos.map(pPeriodo => {
                                  const xValor = xValores?.[pPeriodo.campo];
                                  const xHasValue =
                                    typeof xValor === 'number' && Number.isFinite(xValor);

                                  return (
                                    <MelhorValorTableCell
                                      align="right"
                                      isBest={
                                        xHasValue &&
                                        highlightEnabled &&
                                        utils.comparisons
                                          .getBestValueIndexes(
                                            xAtivos.map(
                                              pOutroAtivo =>
                                                pOutroAtivo.avaliacao?.[pMetrica.campo]?.[
                                                  pPeriodo.campo
                                                ]
                                            ),
                                            pMetrica.criterion
                                          )
                                          .includes(xAtivos.indexOf(pAtivo))
                                      }
                                      key={`${pAtivo.id}-${pMetrica.campo}-${pPeriodo.campo}`}>
                                      {xHasValue ? (
                                        <Stack>
                                          <PrecisionNumberDisplay
                                            value={xValor}
                                            threshold={0.01}
                                            sufix={'%'}
                                          />
                                        </Stack>
                                      ) : (
                                        <Tooltip title="Sem dado" arrow>
                                          <Box
                                            component="span"
                                            tabIndex={0}
                                            sx={{ display: 'inline-flex' }}>
                                            <Typography
                                              component="span"
                                              variant="body2"
                                              color="textSecondary"
                                              fontWeight="bold">
                                              -
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </MelhorValorTableCell>
                                  );
                                })}
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }
);

export default AtivoMetricasRisco;
